import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiShield, FiPlus, FiTrash2, FiEdit2, FiSave, FiX,
  FiCheck, FiAlertTriangle, FiClock, FiUsers, FiFileText,
  FiLock, FiHeart, FiDollarSign, FiBookOpen, FiRefreshCw
} from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Compliance Standards
const COMPLIANCE_STANDARDS = [
  {
    id: 'wtd-weekly-hours',
    category: 'Working Time',
    name: 'Maximum Weekly Working Hours',
    description: 'Employees should not exceed 48 hours per week on average (calculated over 17 weeks) to ensure work-life balance and prevent burnout.',
    howWeComply: 'Shiftry automatically tracks weekly hours and alerts managers when employees approach the 48-hour threshold. Schedule optimization prevents overwork.',
    regulation: 'International Employee Standards',
    value: 48,
    unit: 'hours/week',
    mandatory: true,
    enabled: true,
    icon: 'FiClock',
    color: 'blue'
  },
  {
    id: 'wtd-daily-rest',
    category: 'Working Time',
    name: 'Daily Rest Period',
    description: 'Employees need 11 consecutive hours of rest in each 24-hour period for health and safety.',
    howWeComply: 'Our system enforces minimum rest periods between shifts and flags scheduling conflicts that violate rest requirements.',
    regulation: 'Employee Law Standards',
    value: 11,
    unit: 'hours',
    mandatory: true,
    enabled: true,
    icon: 'FiClock',
    color: 'blue'
  },
  {
    id: 'wtd-breaks',
    category: 'Working Time',
    name: 'Rest Breaks',
    description: 'Employees working more than 6 hours are entitled to a minimum 20-minute break to maintain productivity and wellbeing.',
    howWeComply: 'Break times are built into shift schedules, and our timesheet system tracks and ensures break compliance automatically.',
    regulation: 'Workplace Standards',
    value: 20,
    unit: 'minutes',
    mandatory: true,
    enabled: true,
    icon: 'FiClock',
    color: 'blue'
  },
  {
    id: 'fair-wage',
    category: 'Compensation',
    name: 'Fair Wage Standards',
    description: 'Ensure all employees receive competitive and fair compensation based on role, experience, and market standards.',
    howWeComply: 'Shiftry tracks hourly rates, overtime calculations, and ensures accurate payroll computation. Rate alerts notify when rates fall below configured minimums.',
    regulation: 'Fair Employee Standards',
    value: null,
    unit: 'competitive rates',
    mandatory: true,
    enabled: true,
    icon: 'FiDollarSign',
    color: 'green'
  },
  {
    id: 'attendance-tracking',
    category: 'Accountability',
    name: 'Accurate Time Tracking',
    description: 'Precise attendance and time tracking protects both employer and employee rights and ensures fair compensation.',
    howWeComply: 'Clock in/out system with GPS verification, face recognition, and real-time tracking prevents time theft and ensures accuracy.',
    regulation: 'Payroll Accuracy Standards',
    value: null,
    unit: 'mandatory',
    mandatory: true,
    enabled: true,
    icon: 'FiCheck',
    color: 'purple'
  },
  {
    id: 'leave-management',
    category: 'Time Off',
    name: 'Leave Entitlements',
    description: 'Employees are entitled to paid time off for rest, personal matters, and health recovery.',
    howWeComply: 'Automated leave tracking system manages vacation, sick leave, and personal days. Real-time balance updates ensure transparency.',
    regulation: 'Employee Benefits Standards',
    value: 28,
    unit: 'days/year minimum',
    mandatory: true,
    enabled: true,
    icon: 'FiBookOpen',
    color: 'cyan'
  },
  {
    id: 'data-privacy',
    category: 'Data Protection',
    name: 'Employee Data Privacy',
    description: 'Employee personal information must be securely stored, processed lawfully, and protected from unauthorized access.',
    howWeComply: 'End-to-end encryption for sensitive data, GDPR-compliant storage, role-based access controls, and regular security audits.',
    regulation: 'Data Protection Standards',
    value: null,
    unit: 'encrypted',
    mandatory: true,
    enabled: true,
    icon: 'FiLock',
    color: 'red'
  },
  {
    id: 'equal-opportunity',
    category: 'Workplace Rights',
    name: 'Equal Opportunity & Non-Discrimination',
    description: 'All employees deserve equal treatment regardless of race, gender, age, religion, or disability.',
    howWeComply: 'Fair scheduling algorithms, anonymous performance metrics, and equal opportunity reporting ensure unbiased workforce management.',
    regulation: 'Equal Employment Standards',
    value: null,
    unit: 'mandatory',
    mandatory: true,
    enabled: true,
    icon: 'FiUsers',
    color: 'orange'
  },
  {
    id: 'health-safety',
    category: 'Workplace Safety',
    name: 'Health & Safety Standards',
    description: 'Employers must provide a safe working environment and maintain employee wellbeing.',
    howWeComply: 'Workload monitoring prevents excessive hours, shift patterns ensure adequate rest, and incident reporting tracks workplace safety.',
    regulation: 'Occupational Safety Standards',
    value: null,
    unit: 'ongoing',
    mandatory: true,
    enabled: true,
    icon: 'FiHeart',
    color: 'pink'
  }
];

export default function ComplaintsPage() {
  const { user } = useAuth();
  const [complianceRules, setComplianceRules] = useState(COMPLIANCE_STANDARDS);
  const [filteredRules, setFilteredRules] = useState(COMPLIANCE_STANDARDS);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  // New rule form state
  const [newRule, setNewRule] = useState({
    category: 'Custom',
    name: '',
    description: '',
    regulation: '',
    value: '',
    unit: '',
    mandatory: false,
    enabled: true,
    icon: 'FiFileText',
    color: 'gray'
  });

  useEffect(() => {
    loadComplianceConfig();
  }, []);

  useEffect(() => {
    filterRules();
  }, [selectedCategory, searchTerm, complianceRules]);

  const loadComplianceConfig = async () => {
    try {
      // Load from backend or localStorage
      const savedConfig = localStorage.getItem('complianceConfig');
      if (savedConfig) {
        setComplianceRules(JSON.parse(savedConfig));
      }
    } catch (error) {
      console.error('Error loading compliance config:', error);
    }
  };

  const saveComplianceConfig = async () => {
    try {
      // Save to backend and localStorage
      localStorage.setItem('complianceConfig', JSON.stringify(complianceRules));
      setSaveMessage('Compliance configuration saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving compliance config:', error);
      setSaveMessage('Error saving configuration');
    }
  };

  const filterRules = () => {
    let filtered = complianceRules;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(rule => rule.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(rule =>
        rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.regulation.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRules(filtered);
  };

  const categories = ['All', ...new Set(complianceRules.map(rule => rule.category))];

  const toggleRuleEnabled = (ruleId) => {
    setComplianceRules(prev => prev.map(rule =>
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };

  const addNewRule = () => {
    const rule = {
      ...newRule,
      id: `custom-${Date.now()}`,
      mandatory: false
    };
    setComplianceRules(prev => [...prev, rule]);
    setIsAddingNew(false);
    setNewRule({
      category: 'Custom',
      name: '',
      description: '',
      regulation: '',
      value: '',
      unit: '',
      mandatory: false,
      enabled: true,
      icon: 'FiFileText',
      color: 'gray'
    });
  };

  const updateRule = () => {
    setComplianceRules(prev => prev.map(rule =>
      rule.id === editingRule.id ? editingRule : rule
    ));
    setEditingRule(null);
  };

  const deleteRule = (ruleId) => {
    setComplianceRules(prev => prev.filter(rule => rule.id !== ruleId));
    setShowDeleteConfirm(null);
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all compliance rules to defaults? This will remove any custom rules you have added.')) {
      setComplianceRules(COMPLIANCE_STANDARDS);
      localStorage.removeItem('complianceConfig');
      setSaveMessage('Reset to default compliance rules');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      FiClock, FiShield, FiDollarSign, FiFileText,
      FiLock, FiHeart, FiBookOpen, FiUsers
    };
    const Icon = icons[iconName] || FiFileText;
    return <Icon />;
  };

  const getColorClass = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      green: 'from-green-500 to-green-600',
      purple: 'from-purple-500 to-purple-600',
      orange: 'from-orange-500 to-orange-600',
      red: 'from-red-500 to-red-600',
      pink: 'from-pink-500 to-pink-600',
      indigo: 'from-indigo-500 to-indigo-600',
      gray: 'from-gray-500 to-gray-600'
    };
    return colors[color] || colors.gray;
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FiShield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-600">Access Denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 via-blue-50/20 to-blue-100/10 min-h-screen">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent flex items-center">
              <FiShield className="h-10 w-10 text-blue-600 mr-3" />
              Compliance
            </h1>
            <p className="text-gray-600 mt-2 ml-13">
              Manage and configure compliance requirements for your organization
            </p>
            <div className="mt-3 flex items-center space-x-4 text-sm ml-13">
              <span className="flex items-center text-blue-600 font-semibold">
                <FiCheck className="mr-1" />
                {complianceRules.filter(r => r.enabled).length} Active Rules
              </span>
              <span className="flex items-center text-orange-600 font-semibold">
                <FiAlertTriangle className="mr-1" />
                {complianceRules.filter(r => r.mandatory).length} Mandatory
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={resetToDefaults}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
            >
              <FiRefreshCw className="mr-2" />
              Reset to UK Defaults
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsAddingNew(true)}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
            >
              <FiPlus className="mr-2" />
              Add Custom Rule
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={saveComplianceConfig}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
            >
              <FiSave className="mr-2" />
              Save Configuration
            </motion.button>
          </div>
        </div>

        {/* Save Message */}
        <AnimatePresence>
          {saveMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4 ml-13 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl flex items-center"
            >
              <FiCheck className="mr-2" />
              {saveMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 mb-6"
      >
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[300px]">
            <input
              type="text"
              placeholder="Search compliance rules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Compliance Rules Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredRules.map((rule, index) => (
          <motion.div
            key={rule.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`bg-white rounded-2xl shadow-lg border-2 ${
              rule.enabled ? 'border-blue-200' : 'border-gray-200'
            } overflow-hidden`}
          >
            {/* Card Header */}
            <div className={`bg-gradient-to-r ${getColorClass(rule.color)} p-4 text-white`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center flex-1">
                  <div className="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-3">
                    {getIconComponent(rule.icon)}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold opacity-90">{rule.category}</p>
                    <h3 className="text-lg font-bold">{rule.name}</h3>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {rule.mandatory && (
                    <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs font-bold">
                      MANDATORY
                    </span>
                  )}
                  
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={() => toggleRuleEnabled(rule.id)}
                      disabled={rule.mandatory}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-white/30 peer-focus:outline-none rounded-full peer ${
                      rule.mandatory ? 'cursor-not-allowed opacity-50' : ''
                    } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">{rule.description}</p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <span className="text-sm font-semibold text-gray-700">Regulation:</span>
                  <span className="text-sm text-blue-700 font-semibold">{rule.regulation}</span>
                </div>
                
                {rule.value && (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                    <span className="text-sm font-semibold text-gray-700">Threshold:</span>
                    <span className="text-lg text-green-700 font-bold">
                      {rule.value} {rule.unit}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                {!rule.mandatory && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setEditingRule(rule)}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-all font-semibold"
                    >
                      <FiEdit2 className="mr-2" />
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowDeleteConfirm(rule.id)}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-all font-semibold"
                    >
                      <FiTrash2 className="mr-2" />
                      Delete
                    </motion.button>
                  </>
                )}
                {rule.mandatory && (
                  <div className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-500 rounded-xl font-semibold">
                    <FiLock className="mr-2" />
                    UK Legal Requirement
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add New Rule Modal */}
      <AnimatePresence>
        {isAddingNew && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setIsAddingNew(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Add Custom Compliance Rule</h2>
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select
                    value={newRule.category}
                    onChange={(e) => setNewRule({ ...newRule, category: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Custom">Custom</option>
                    <option value="Working Time Directive">Working Time Directive</option>
                    <option value="National Minimum Wage">National Minimum Wage</option>
                    <option value="Health & Safety">Health & Safety</option>
                    <option value="Data Protection">Data Protection</option>
                    <option value="Safeguarding">Safeguarding</option>
                    <option value="Immigration">Immigration</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Rule Name</label>
                  <input
                    type="text"
                    value={newRule.name}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Custom Training Requirement"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the compliance requirement..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Regulation/Standard</label>
                  <input
                    type="text"
                    value={newRule.regulation}
                    onChange={(e) => setNewRule({ ...newRule, regulation: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Internal Company Policy"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Value (Optional)</label>
                    <input
                      type="text"
                      value={newRule.value}
                      onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Unit (Optional)</label>
                    <input
                      type="text"
                      value={newRule.unit}
                      onChange={(e) => setNewRule({ ...newRule, unit: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., hours/week"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Icon</label>
                    <select
                      value={newRule.icon}
                      onChange={(e) => setNewRule({ ...newRule, icon: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="FiFileText">Document</option>
                      <option value="FiClock">Clock</option>
                      <option value="FiShield">Shield</option>
                      <option value="FiDollarSign">Dollar</option>
                      <option value="FiHeart">Heart</option>
                      <option value="FiLock">Lock</option>
                      <option value="FiBookOpen">Book</option>
                      <option value="FiUsers">Users</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Color</label>
                    <select
                      value={newRule.color}
                      onChange={(e) => setNewRule({ ...newRule, color: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="blue">Blue</option>
                      <option value="green">Green</option>
                      <option value="purple">Purple</option>
                      <option value="orange">Orange</option>
                      <option value="red">Red</option>
                      <option value="pink">Pink</option>
                      <option value="indigo">Indigo</option>
                      <option value="gray">Gray</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={addNewRule}
                    disabled={!newRule.name || !newRule.description}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiPlus className="inline mr-2" />
                    Add Rule
                  </motion.button>
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Rule Modal */}
      <AnimatePresence>
        {editingRule && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setEditingRule(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Edit Compliance Rule</h2>
                  <button
                    onClick={() => setEditingRule(null)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Rule Name</label>
                  <input
                    type="text"
                    value={editingRule.name}
                    onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editingRule.description}
                    onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Value</label>
                    <input
                      type="text"
                      value={editingRule.value || ''}
                      onChange={(e) => setEditingRule({ ...editingRule, value: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Unit</label>
                    <input
                      type="text"
                      value={editingRule.unit}
                      onChange={(e) => setEditingRule({ ...editingRule, unit: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={updateRule}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
                  >
                    <FiSave className="inline mr-2" />
                    Save Changes
                  </motion.button>
                  <button
                    onClick={() => setEditingRule(null)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiAlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Compliance Rule?</h3>
                <p className="text-gray-600 mb-6">
                  This action cannot be undone. The compliance rule will be permanently removed.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => deleteRule(showDeleteConfirm)}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {filteredRules.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-20"
        >
          <FiShield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No compliance rules found</h3>
          <p className="text-gray-500">Try adjusting your filters or add a new rule</p>
        </motion.div>
      )}
    </div>
  );
}
